import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import useMobileVh from '../../../Hooks///UseMobileVh';
import { v4 as uuid } from 'uuid';
import Button from '../Button/Button';
import { Variant, Size } from '../../../Constants/System';
import InlineSpinner from '../../Loading/InlineSpinner';
import { CombineClasses } from '../../../Utility/ReactUtils';
export default function ModalContent(props) {
    useMobileVh();
    const [height, setHeight] = useState();
    const contentRef = useRef(null);
    useEffect(() => {
        if (!contentRef?.current)
            return;
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.contentBoxSize) {
                    if (contentRef.current) {
                        setHeight(contentRef.current.clientHeight);
                    }
                }
            }
        });
        resizeObserver.observe(contentRef.current);
        return () => {
            resizeObserver.disconnect();
        };
    }, [contentRef]);
    const actionsWithIds = useMemo(() => {
        return props.actions?.map((action) => {
            return {
                ...action,
                key: uuid(),
            };
        });
    }, [props.actions]);
    const cta = props.cta === undefined ? false : props.cta;
    let innerContent = null;
    if (props.isLoading) {
        innerContent = (_jsx(LoadingContainer, { children: _jsx(InlineSpinner, { overlay: true }) }));
    }
    else {
        innerContent = (_jsxs(_Fragment, { children: [props.headerImg ? _jsx("img", { src: props.headerImg, className: "header-image" }) : null, _jsxs(TextContent, { children: [_jsxs(Content, { "$cta": cta, children: [props.title ? _jsx(ModalTitle, { children: props.title }) : null, props.subtitle ? _jsx(ModalSubtitle, { children: props.subtitle }) : null, props.text ? _jsx("p", { className: "modal-text", children: props.text }) : '', props.body ? _jsx(ModalBody, { children: props.body }) : null] }), actionsWithIds && actionsWithIds.length !== 0 && (_jsx(Actions, { "$cta": cta, children: actionsWithIds?.map((action, index) => {
                                if (!action.variant) {
                                    action.variant = Variant.solid;
                                }
                                return !action.child ? (_jsx(Button, { className: action.className, onClick: action.callback, variant: action.variant, size: Size.medium, scheme: action.scheme, disabled: action.disabled, children: action.text }, action.key)) : (_jsx("div", { children: action.child }, index));
                            }) })), props.footer ? props.footer : null] })] }));
    }
    return (_jsx(AnimationContainer, { "$height": height, children: _jsx(StyledModalContent, { ref: contentRef, "$cta": cta, className: CombineClasses(props.className, 'modal-content'), children: innerContent }) }));
}
const LoadingContainer = styled.div `
	width: 250px;
	height: 250px;
`;
const AnimationContainer = styled.div `
	${(props) => (props.$height ? `max-height: ${props.$height}px; min-height: ${props.$height}px;` : '')}
	transition: max-height 300ms, min-height 300ms;
	overflow: hidden;

	@media (max-width: 600px) {
		min-height: 100vh;
		max-height: 100vh;
		min-height: calc(var(--vh, 1vh) * 100);
		max-height: calc(var(--vh, 1vh) * 100);
	}
`;
const StyledModalContent = styled.div `
	color: ${(props) => (props.$cta ? props.theme.palette.primary : props.theme.palette.secondary)};
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: stretch;

	.modal-text {
		font-family: lato, sans-serif;
		font-size: ${(props) => props.theme.typography.md};
		font-weight: ${(props) => props.theme.typography.FontWeight.regular};
		line-height: 1.6;
		margin-bottom: 0;
	}

	.header-image {
		min-height: 200px;

		max-width: 100%;
		max-height: 60vh;
		height: auto;

		display: block;
		margin: auto;

		@media (max-height: 800px) {
			max-height: 50vh;
		}

		@media (max-height: 500px) and (min-width: 800px) {
			max-height: 80vh;
		}
	}

	@media (max-height: 800px) and (min-width: 800px) {
		flex-direction: row;
	}

	@media (max-width: 600px) {
		min-height: 100vh;
		min-height: calc(var(--vh, 1vh) * 100);
	}
`;
const TextContent = styled.div `
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: stretch;
	flex-grow: 1;
`;
const Content = styled.div `
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	background-color: ${(props) => (props.$cta ? 'transparent' : props.theme.palette.shade6)};
	padding: ${(props) => props.theme.spacing.lg};
`;
const Actions = styled.div `
	box-sizing: border-box;
	background-color: ${(props) => (props.$cta ? 'transparent' : props.theme.palette.shade7)};
	width: 100%;
	display: flex;
	justify-content: flex-end;

	padding: ${(props) => props.theme.spacing.lg};

	button {
		margin-left: ${(props) => props.theme.spacing.xs};
	}
`;
const ModalTitle = styled.h2 `
	box-sizing: border-box;
	padding: 0;
	margin: 0;
	font-size: ${(props) => props.theme.typography.xl};
	font-weight: ${(props) => props.theme.typography.FontWeight.bold};
	margin-bottom: ${(props) => props.theme.spacing.xs};
	margin-right: ${(props) => props.theme.spacing.lg};
	font-family: lato, sans-serif;
`;
const ModalSubtitle = styled.h4 `
	box-sizing: border-box;
	padding: 0;
	margin: 0;
	font-size: ${(props) => props.theme.typography.lg};
	font-weight: ${(props) => props.theme.typography.FontWeight.regular};
	color: ${(props) => props.theme.palette.shade4};
`;
const ModalBody = styled.div `
	height: 100%;
	width: 100%;
	max-height: 75vh;
	overflow-y: auto;
	${(props) => props.theme.scrollbars.skinny}
`;
//# sourceMappingURL=ModalContent.js.map